import { Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, FormLabel, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Radio, RadioGroup, Select, TextField, Typography } from '@mui/material'
import { useEffect, useMemo } from 'react';
import { validarCpfCnpj } from '../../../../../helpers/verificadores';
import { formatarCEP, formatarCPFCNPJ, formatarTelefone, permitirApenasNumerosOuSN } from '../../../../../helpers/masks';
import moment, { Moment } from 'moment';
import BasicDatePicker from '../../../../BasicDatePicker';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import SelectNacionalidade from '../SelectNacionalidade';
import { useQuery } from 'react-query';
import axiosClient from '../../../../../lib/axios';
import { desformatarCEP } from '../../../../../helpers/unmask';
import { ViaCEPResposta } from '../../../../../types/externas/viacep-resultado';
import SearchIcon from '@mui/icons-material/Search';
import DadosConjugeSocio from './DadosConjugeSocio';
import { validarTelefone } from '../../../../../helpers/validarTelefone';

export default function DialogAddEditDadoSocio({ open, index, onClose }: DialogAddEditDadoSocioProps) {

    const { setValue: setMainFormValue, getValues: getMainFormValues } = useFormContext();

    const isEdit = typeof index === 'number';

    const { control, handleSubmit, reset, watch, getValues, setValue } = useForm<ISocioForm>({
        defaultValues: defaultValues,
        mode: 'onSubmit',
        reValidateMode: 'onBlur',
    });

    const ufs = useMemo(() => ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"], [])

    //queries
    const { data } = useQuery({
        queryKey: ["pesquisar-cidades", watch(`uf`)],
        queryFn: async () => {
            if (watch(`uf`) === undefined) return []
            let result = await axiosClient.get("/pesquisar-cidades", {
                params: {
                    uf: getValues(`uf`)
                }
            })
            return result?.data
        }
    });
    const { dataCidades }: ICidades = {
        dataCidades: data || []
    }
    //

    //funções manipuladoras
    const handlePesquisarCep = async () => {
        const { cep } = getValues();
        if (cep === undefined || cep === "") return

        const unmaskCep = desformatarCEP(cep)

        if (unmaskCep.length !== 8) {
            enqueueSnackbar('CEP inválido. Deve conter 8 dígitos.', { variant: 'error' })
            return
        }

        try {
            const response = await axiosClient.get<ViaCEPResposta>(`https://viacep.com.br/ws/${unmaskCep}/json/`);
            const { bairro, logradouro, localidade, uf } = response.data
            setValue(`uf`, uf, { shouldValidate: true, shouldDirty: true })
            setValue(`bairro`, bairro, { shouldValidate: true, shouldDirty: true })
            setValue(`cidade`, localidade, { shouldValidate: true, shouldDirty: true })
            setValue(`endereco`, logradouro, { shouldValidate: true, shouldDirty: true })
        } catch (error) {
            enqueueSnackbar('Erro ao buscar endereço por CEP: ' + error, { variant: 'error' })
            return
        }
    }

    const possuiConjuge = watch(`possui_conjuge`) === "Sim"

    const tipoSocio = watch('tipo_socio');

    const opcoesRadio = useMemo(() => [
        { value: "Sim", label: "Sim" },
        { value: "Não", label: "Não" },
    ], [])

    const opcoesTipoPessoaRadio = useMemo(() => [
        { value: "F", label: "Física" },
        { value: "J", label: "Jurídica" },
    ], []);

    const opcoesSexo = useMemo(() => [
        { value: "M", label: "Masculino" },
        { value: "F", label: "Feminino" },
    ], []);

    const opcoesGrauParentesco = useMemo(() => [
        { value: "Pai", label: "Pai" },
        { value: "Mãe", label: "Mãe" },
        { value: "Filho", label: "Filho" },
        { value: "Irmão", label: "Irmão" },
        { value: "Outros", label: "Outros" },
    ], []);

    useEffect(() => {
        if (open && isEdit) {
            const sociedadeAtual = getMainFormValues(`dados_cadastrais.sociedades.${index}`);
            if (sociedadeAtual) {
                reset({
                    ...sociedadeAtual,
                    // Convert string date to Moment object if it exists
                    data_nascimento: sociedadeAtual.data_nascimento
                        ? moment(sociedadeAtual.data_nascimento, 'YYYY-MM-DD')
                        : null
                });
            }
        } else if (open && !isEdit) {
            reset(defaultValues);
        }
    }, [open, isEdit, index, reset, getMainFormValues]);

    const handleSave = handleSubmit((data) => {
        try {
            const formattedData = {
                ...data,
                data_nascimento: data.data_nascimento
                    ? moment(data.data_nascimento).format("YYYY-MM-DD")
                    : null
            };

            if (isEdit) {
                setMainFormValue(`dados_cadastrais.sociedades.${index}`, formattedData);
                enqueueSnackbar('Sócio atualizado com sucesso!', { variant: 'success' });
            } else {
                const sociedadesAtuais = getMainFormValues('dados_cadastrais.sociedades') || [];
                setMainFormValue('dados_cadastrais.sociedades', [...sociedadesAtuais, formattedData]);
                enqueueSnackbar('Sócio adicionado com sucesso!', { variant: 'success' });
            }
            onClose();
        } catch (error) {
            enqueueSnackbar('Erro ao salvar sócio', { variant: 'error' });
            console.error('Erro ao salvar sócio:', error);
        }
    }, (errors) => {
        enqueueSnackbar('Por favor, corrija os erros no formulário', { variant: 'error' });
    });

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="lg"
        >
            <DialogTitle><b>{isEdit ? 'Editar' : 'Adicionar'} Sócio</b></DialogTitle>
            <DialogContent>
                <Grid container spacing={2} mb={2} mt={1}>
                    <Grid size={{ xs: 12 }}>
                        <Typography fontWeight={'bold'}>Dados Básicos</Typography>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                        <Controller
                            name="tipo_socio"
                            control={control}
                            rules={{ required: 'Campo obrigatório' }}
                            render={({ field, fieldState: { error, invalid } }) => (
                                <FormControl {...field} required error={invalid}>
                                    <FormLabel id="tipo_pessoa">Tipo de pessoa</FormLabel>
                                    <RadioGroup
                                        row
                                        aria-labelledby="tipo_pessoa"
                                        value={field.value}
                                        onChange={(e) => {
                                            field.onChange(e.target.value as "F" | "J");
                                            reset({
                                                ...defaultValues,
                                                tipo_socio: e.target.value as "F" | "J"
                                            })
                                        }}
                                    >
                                        {
                                            opcoesTipoPessoaRadio.map((opcao, index) => (
                                                <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                            ))
                                        }
                                    </RadioGroup>
                                    {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                                </FormControl>
                            )}
                        />
                    </Grid>
                </Grid>
                {
                    tipoSocio === "F" &&
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="nome"
                                control={control}
                                rules={{ required: 'O nome do sócio é obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        required
                                        label="Nome"
                                        fullWidth
                                        error={invalid}
                                        helperText={error?.message || ""}
                                        slotProps={{ htmlInput: { maxLength: 40 } }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="nome_social"
                                control={control}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        label="Nome Social"
                                        fullWidth
                                        slotProps={{ htmlInput: { maxLength: 40 } }}
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 3 }}>
                            <Controller
                                name="rg"
                                control={control}
                                rules={{ required: 'O campo é obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        required
                                        label='RG'
                                        type="text"
                                        fullWidth
                                        slotProps={{ htmlInput: { maxLength: 20 } }}
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 3 }}>
                            <Controller
                                name="cpf"
                                control={control}
                                rules={{
                                    required: 'O CPF do sócio é obrigatório',
                                    validate: (value) => {
                                        if (!value || value.trim() === "") return "O CPF do sócio é obrigatório";
                                        return validarCpfCnpj(value) || 'CPF inválido';
                                    }
                                }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        required
                                        label="CPF"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) => field.onChange(formatarCPFCNPJ(e.target.value))}
                                        error={invalid}
                                        helperText={error?.message || ""}
                                        slotProps={{ htmlInput: { maxLength: 17 } }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 3 }}>
                            <Controller
                                name="sexo"
                                control={control}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <FormControl fullWidth error={invalid}>
                                        <InputLabel id="sexo-select">Sexo</InputLabel>
                                        <Select
                                            {...field}
                                            labelId="sexo-select"
                                            label="Sexo"
                                        >
                                            {opcoesSexo.map((opcao, index) => (
                                                <MenuItem key={index} value={opcao.value}>{opcao.label}</MenuItem>
                                            ))}
                                        </Select>
                                        {invalid && <FormHelperText>{error?.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 3 }}>
                            <Controller
                                name="telefone"
                                control={control}
                                rules={{
                                    maxLength: { value: 20, message: 'Telefone deve ter no máximo 20 caracteres' },
                                    validate: (value) => {
                                        if (!value || value.trim() === "") return true;
                                        const telefoneValido = validarTelefone(value);
                                        return telefoneValido || "Telefone inválido";
                                    }
                                }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        label="Telefone"
                                        placeholder="Telefone"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) => field.onChange(formatarTelefone(e.target.value))}
                                        error={invalid}
                                        helperText={error?.message || ""}
                                        slotProps={{ htmlInput: { maxLength: 20 } }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Controller
                                name={`nome_mae`}
                                control={control}
                                rules={{ required: "Campo obrigatório" }}
                                render={({ field, fieldState: { error, invalid } }) => {
                                    return (
                                        <TextField
                                            {...field}
                                            required
                                            error={invalid}
                                            label="Nome da Mãe"
                                            fullWidth
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                            helperText={error ? error.message : ""}
                                        />
                                    );
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                            <Controller
                                name={`nome_pai`}
                                control={control}
                                render={({ field }) => {
                                    return (
                                        <TextField
                                            {...field}
                                            label="Nome do Pai"
                                            fullWidth
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.value)}
                                        />
                                    );
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                                name="data_nascimento"
                                control={control}
                                rules={{
                                    required: 'A data de nascimento do sócio é obrigatória',
                                    validate: (value) => {
                                        if (!value) return "A data de nascimento do sócio é obrigatória";
                                        const dataMoment = moment(value);
                                        if (!dataMoment.isValid()) return "Data inválida";
                                        if (dataMoment.isAfter(moment())) return "Data não pode ser futura";
                                        return true;
                                    }
                                }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <BasicDatePicker
                                        required
                                        label="Data de Nascimento"
                                        value={field.value}
                                        blockFutureDates
                                        futureDates={moment().subtract(1, 'days')}
                                        onChange={(data) => field.onChange(data)}
                                        formatString="DD/MM/YYYY"
                                        readonly={false}
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                                name="nacionalidade"
                                control={control}
                                rules={{ required: 'A nacionalidade do sócio é obrigatória' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <SelectNacionalidade
                                        required
                                        value={field.value}
                                        onChange={(value) => field.onChange(value)}
                                        error={invalid}
                                        errorMessage={error ? error.message : undefined}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                                name="grau_parentesco"
                                control={control}
                                rules={{ required: 'O grau de parentesco do sócio é obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <FormControl fullWidth error={invalid}>
                                        <InputLabel id="grau-parentesco-select">Grau de Parentesco</InputLabel>
                                        <Select
                                            {...field}
                                            labelId="grau-parentesco-select"
                                            label="Grau de Parentesco"
                                        >
                                            {opcoesGrauParentesco.map((opcao, index) => (
                                                <MenuItem key={index} value={opcao.value}>{opcao.label}</MenuItem>
                                            ))}
                                        </Select>
                                        {error && <FormHelperText>{error.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 6, md: 3 }}>
                            <Controller
                                name={`possui_conjuge`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <FormControl {...field} required error={invalid}>
                                        <FormLabel id="possui_conjuge">Possui cônjuge/companheiro?</FormLabel>
                                        <RadioGroup
                                            row
                                            aria-labelledby="Possui cônjuge/companheiro ?"
                                            value={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        >
                                            {
                                                opcoesRadio.map((opcao, index) => (
                                                    <FormControlLabel key={index} value={opcao.value} control={<Radio />} label={opcao.label} />
                                                ))
                                            }
                                        </RadioGroup>
                                        {invalid && <FormHelperText error>{error?.message}</FormHelperText>}
                                    </FormControl>
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Typography fontWeight={'bold'}>Endereço</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                            <Controller
                                name={`cep`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="CEP"
                                        variant="outlined"
                                        fullWidth
                                        required
                                        onChange={(e) => field.onChange(formatarCEP(e.target.value))}
                                        onBlur={handlePesquisarCep}
                                        error={fieldState.invalid}
                                        helperText={fieldState.error?.message || ""}
                                        slotProps={{
                                            input: {
                                                endAdornment: <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handlePesquisarCep}
                                                        edge="end"
                                                    >
                                                        <SearchIcon />
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name={`endereco`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="Endereço"
                                        variant="outlined"
                                        required
                                        fullWidth
                                        error={fieldState.invalid}
                                        helperText={fieldState.error?.message || ""}
                                        slotProps={{
                                            inputLabel: { shrink: field.value ? true : undefined }
                                        }}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 2 }}>
                            <Controller
                                name={`numero`}
                                control={control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="Número"
                                        type="text"
                                        variant="outlined"
                                        fullWidth
                                        onChange={(e) => {
                                            field.onChange(permitirApenasNumerosOuSN(e.target.value))
                                        }}
                                        error={fieldState.invalid}
                                        helperText={fieldState.error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 3 }}>
                            <Controller
                                name={`complemento`}
                                control={control}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="Complemento"
                                        variant="outlined"
                                        fullWidth
                                        error={fieldState.invalid}
                                        helperText={fieldState.error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>


                        <Grid size={{ xs: 12, md: 3 }}>
                            <Controller
                                name={`bairro`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState }) => (
                                    <TextField
                                        {...field}
                                        label="Bairro"
                                        variant="outlined"
                                        required
                                        fullWidth
                                        error={fieldState.invalid}
                                        helperText={fieldState.error?.message || ""}
                                        slotProps={{
                                            inputLabel: { shrink: field.value ? true : undefined }
                                        }}
                                    />
                                )}
                            />
                        </Grid>


                        <Grid size={{ xs: 12, md: 2 }}>
                            <Controller
                                name={`uf`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState }) => (
                                    <FormControl fullWidth error={fieldState.invalid} required>
                                        <InputLabel id="uf-socio-select">UF</InputLabel>
                                        <Select
                                            labelId="uf-socio-select"
                                            value={field.value || ''}
                                            label="UF"
                                            onChange={(e) => field.onChange(e.target.value)}
                                        >
                                            {ufs.map((uf, index) => (
                                                <MenuItem key={index} value={uf}>{uf}</MenuItem>
                                            ))}
                                        </Select>
                                        {fieldState.error && (
                                            <FormHelperText>{fieldState.error?.message}</FormHelperText>
                                        )}
                                    </FormControl>
                                )}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 4 }}>
                            <Controller
                                name={`cidade`}
                                control={control}
                                rules={{ required: 'Campo obrigatório' }}
                                render={({ field, fieldState }) => (
                                    <Autocomplete
                                        value={field.value || null}
                                        fullWidth
                                        disablePortal
                                        options={dataCidades}
                                        onChange={(_, v) => field.onChange(v || '')}
                                        renderInput={(params) =>
                                            <TextField
                                                {...params}
                                                label="Cidade"
                                                required
                                                error={fieldState.invalid}
                                                helperText={fieldState.error?.message || ""}
                                            />
                                        }
                                    />
                                )}
                            />
                        </Grid>
                        {
                            possuiConjuge &&
                            <Grid container size={{ xs: 12 }} spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                    <Typography fontWeight={'bold'} variant="h6">Dados do Cônjuge/Companheiro(a)</Typography>
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Typography fontWeight={'bold'} >Dados básicos</Typography>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Controller
                                        name={`nome_conjuge`}
                                        control={control}
                                        rules={{ required: "Campo obrigatório" }}
                                        render={({ field, fieldState: { error, invalid } }) => {
                                            return (
                                                <TextField
                                                    {...field}
                                                    required
                                                    error={invalid}
                                                    label="Nome"
                                                    fullWidth
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    slotProps={{ htmlInput: { maxLength: 40 } }}
                                                    helperText={error ? error.message : ""}
                                                />
                                            );
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Controller
                                        name={`nome_social_conjuge`}
                                        control={control}
                                        render={({ field, fieldState: { error, invalid } }) => {
                                            return (
                                                <TextField
                                                    {...field}
                                                    error={invalid}
                                                    label="Nome Social"
                                                    fullWidth
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    slotProps={{ htmlInput: { maxLength: 40 } }}
                                                    helperText={error ? error.message : ""}
                                                />
                                            );
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                    <Controller
                                        name={`rg_conjuge`}
                                        control={control}
                                        rules={{ required: "Campo obrigatório" }}
                                        render={({ field, fieldState: { error, invalid } }) => (
                                            <TextField
                                                {...field}
                                                required
                                                label='RG'
                                                type="text"
                                                fullWidth
                                                value={field.value}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                error={invalid}
                                                helperText={error ? error.message : ""}
                                                slotProps={{ htmlInput: { maxLength: 20 } }}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                    <Controller
                                        name={`cpf_conjuge`}
                                        control={control}
                                        rules={{
                                            required: "Campo obrigatório",
                                            validate: (value) => {
                                                if (!possuiConjuge) return true;
                                                return validarCpfCnpj(value ?? '') || 'CPF inválido';
                                            },
                                        }}
                                        render={({ field, fieldState: { error, invalid } }) => {
                                            return (
                                                <TextField
                                                    {...field}
                                                    required
                                                    error={invalid}
                                                    label="CPF"
                                                    fullWidth
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(formatarCPFCNPJ(e.target.value))}
                                                    slotProps={{ htmlInput: { maxLength: 17 } }}
                                                    helperText={error ? error.message : ""}
                                                />
                                            );
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                    <Controller
                                        name={`sexo_conjuge`}
                                        control={control}
                                        rules={{ required: "Campo obrigatório" }}
                                        render={({ field, fieldState: { error, invalid } }) => {
                                            return (
                                                <FormControl fullWidth error={invalid} required>
                                                    <InputLabel id="sexo-select">Sexo</InputLabel>
                                                    <Select
                                                        {...field}
                                                        labelId="sexo-select"
                                                        value={field.value}
                                                        label="Sexo"
                                                    >
                                                        <MenuItem value="M">Masculino</MenuItem>
                                                        <MenuItem value="F">Feminino</MenuItem>
                                                    </Select>
                                                    <FormHelperText error>{error ? error.message : ""}</FormHelperText>
                                                </FormControl>
                                            );
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Controller
                                        name={`nome_mae_conjuge`}
                                        control={control}
                                        rules={{ required: "Campo obrigatório" }}
                                        render={({ field, fieldState: { error, invalid } }) => {
                                            return (
                                                <TextField
                                                    {...field}
                                                    required
                                                    error={invalid}
                                                    label="Nome da Mãe"
                                                    fullWidth
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    helperText={error ? error.message : ""}
                                                />
                                            );
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Controller
                                        name={`nome_pai_conjuge`}
                                        control={control}
                                        render={({ field }) => {
                                            return (
                                                <TextField
                                                    {...field}
                                                    label="Nome do Pai"
                                                    fullWidth
                                                    value={field.value}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                />
                                            );
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Controller
                                        name={`data_nascimento_conjuge`}
                                        control={control}
                                        rules={{
                                            required: 'Campo obrigatório',
                                            validate: (value) => {
                                                const dataMoment = moment(value, "YYYY-MM-DD", true);
                                                if (!dataMoment.isValid()) return "Data inválida";
                                                if (dataMoment.isAfter(moment())) return "Data não pode ser futura";
                                                return true;
                                            }
                                        }}
                                        render={({ field, fieldState: { error, invalid } }) => (
                                            <BasicDatePicker
                                                required
                                                label="Data de Nascimento"
                                                helperText={error ? error.message : ""}
                                                error={invalid}
                                                value={field.value ? moment(field.value, "YYYY-MM-DD") : null}
                                                blockFutureDates
                                                futureDates={moment().subtract(1, 'days')}
                                                onChange={(data) => {
                                                    field.onChange(data ? moment(data).format("YYYY-MM-DD") : null);
                                                }}
                                                formatString="DD/MM/YYYY"
                                                readonly={false}
                                            />
                                        )}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Controller
                                        name={`nacionalidade_conjuge`}
                                        control={control}
                                        rules={{ required: 'Campo obrigatório' }}
                                        render={({ field, fieldState: { error, invalid } }) => (
                                            <SelectNacionalidade
                                                required
                                                value={field.value}
                                                onChange={(value) => field.onChange(value)}
                                                error={invalid}
                                                errorMessage={error ? error.message : undefined}
                                            />
                                        )}
                                    />
                                </Grid>
                                <DadosConjugeSocio {...{ control, watch, getValues, setValue }} />
                            </Grid>
                        }
                    </Grid>
                }
                {
                    tipoSocio === "J" &&
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="cnpj"
                                control={control}
                                rules={{
                                    required: 'O CNPJ do sócio é obrigatório',
                                    validate: (value) => {
                                        if (!value || value.trim() === "") return "O CNPJ do sócio é obrigatório";
                                        return validarCpfCnpj(value) || 'CNPJ inválido';
                                    }
                                }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        required
                                        label="CNPJ"
                                        fullWidth
                                        value={field.value}
                                        onChange={(e) => field.onChange(formatarCPFCNPJ(e.target.value))}
                                        error={invalid}
                                        helperText={error?.message || ""}
                                        slotProps={{ htmlInput: { maxLength: 17 } }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="nome"
                                control={control}
                                rules={{ required: 'A razão social é obrigatória' }}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        required
                                        label="Razão Social"
                                        fullWidth
                                        slotProps={{ htmlInput: { maxLength: 40 } }}
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Controller
                                name="nome_social"
                                control={control}
                                render={({ field, fieldState: { error, invalid } }) => (
                                    <TextField
                                        {...field}
                                        required
                                        label="Nome Fantasia"
                                        fullWidth
                                        slotProps={{ htmlInput: { maxLength: 40 } }}
                                        error={invalid}
                                        helperText={error?.message || ""}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                }
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                >
                    Salvar
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DialogAddEditDadoSocioProps {
    open: boolean;
    index?: number;
    onClose: () => void;
}

export interface ISocioForm {
    tipo_socio: "F" | "J";
    nome: string;
    nome_social: string;
    telefone?: string;
    rg: string;
    cpf: string;
    sexo: string;
    data_nascimento: Moment | null;
    nacionalidade: string;
    cnpj: string;
    nome_mae: string;
    nome_pai?: string;
    grau_parentesco?: string;
    possui_conjuge: string;
    //Endereço
    cep?: string;
    endereco?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    uf?: string;
    //Dados do conjuge
    nome_conjuge?: string;
    nome_social_conjuge?: string;
    nome_mae_conjuge?: string;
    nome_pai_conjuge?: string;
    rg_conjuge?: string;
    cpf_conjuge?: string;
    sexo_conjuge?: string;
    data_nascimento_conjuge?: Moment | null;
    nacionalidade_conjuge: string;
    //Endereço do conjuge
    cep_conjuge?: string;
    endereco_conjuge?: string;
    numero_conjuge?: string;
    complemento_conjuge?: string;
    bairro_conjuge?: string;
    cidade_conjuge?: string;
    uf_conjuge?: string;
}

const defaultValues: ISocioForm = {
    tipo_socio: "F",
    nome: '',
    nome_social: '',
    rg: '',
    cpf: '',
    sexo: '',
    data_nascimento: null,
    nacionalidade: 'Brasileiro(a)',
    cnpj: '',
    nome_mae: '',
    nome_pai: '',
    grau_parentesco: '',
    possui_conjuge: 'Não',
    nacionalidade_conjuge: 'Brasileiro(a)',
};

interface ICidades {
    dataCidades: Array<string>
}